import { useEffect, useState } from 'react'
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded'
import { HelperDashboardLayout } from './components/Helper/HelperDashboardLayout'
import { RoleProtectedRoute } from './components/Auth/RoleProtectedRoute'
import { ToastProvider } from './context/ToastContext'
import { ToastViewport } from './components/Toast/ToastViewport'
import { WorkspaceSwitchGuardSheet } from './components/Common/WorkspaceSwitchGuardSheet'
import { WorkspaceAccessLostSheet } from './components/Common/WorkspaceAccessLostSheet'
import { Button } from './components/Common/Button'
import { MobileFullScreenPanel } from './components/Common/MobileFullScreenPanel'
import { ExitConfirmSheet } from './components/Common/ExitConfirmSheet'
import { IncomingInvitesPage } from './components/Invites/IncomingInvitesPage'
import { BackStackProvider } from './context/BackStackContext'
import { useNativeBackButton } from './hooks/useNativeBackButton'
import {
  startSyncWorker,
  stopSyncWorker,
} from './services/helper/syncWorker'
import { parseJoinToken, stashPendingLinkToken } from './services/inviteService'
import { useAuth } from './context/useAuth'
import { useWorkspace } from './context/useWorkspace'
import { useNls } from './nls/nlsProvider'
import { helperViewFromPathname, type HelperRouteView as HelperShellView } from './components/Helper/helperShellRouting'

const captureJoinTokenFromIntent = () => {
  if (typeof window === 'undefined') return
  const candidates: string[] = []
  if (typeof window.location?.href === 'string') candidates.push(window.location.href)
  const preload = (window as unknown as { __APP_SHELL_INTENT__?: { url?: string } })
    .__APP_SHELL_INTENT__
  if (preload?.url) candidates.push(preload.url)

  for (const candidate of candidates) {
    const token = parseJoinToken(candidate)
    if (token) {
      stashPendingLinkToken(token)
      return
    }
  }
}

const helperViewFromPath = (): HelperShellView => {
  if (typeof window === 'undefined') return 'TASKS'
  return helperViewFromPathname(window.location.pathname)
}

const HelperNoWorkspaceShell = () => {
  const { t } = useNls()
  const { logout } = useAuth()
  const [incomingInvitesOpen, setIncomingInvitesOpen] = useState(false)
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false)

  useNativeBackButton({
    onEmpty: () => {
      setExitConfirmOpen(true)
    },
  })

  return (
    <>
      <main className="min-h-screen bg-[var(--bg)] px-6 py-10 text-[var(--text)]">
        <div className="mx-auto flex min-h-[70vh] max-w-sm flex-col items-center justify-center gap-4 text-center">
          <div className="grid h-20 w-20 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface-muted)] text-[var(--muted)]">
            <HourglassEmptyRoundedIcon style={{ fontSize: 32 }} />
          </div>
          <div className="space-y-2">
            <h1 className="text-lg font-semibold text-[var(--text-strong)]">{t('NLSHelperNoWorkspaceTitle')}</h1>
            <p className="text-sm leading-relaxed text-[var(--muted)]">
              {t('NLSHelperNoWorkspaceDescription')}
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 pt-2">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => setIncomingInvitesOpen(true)}
            >
              {t('NLSHelperIncomingInvites')}
            </Button>
            <Button
              variant="tertiary"
              size="lg"
              className="w-full"
              onClick={() => void logout()}
            >
              {t('NLSHelperSignOut')}
            </Button>
          </div>
        </div>
      </main>
      {incomingInvitesOpen ? (
        <MobileFullScreenPanel title={t('NLSHelperIncomingInvites')} onClose={() => setIncomingInvitesOpen(false)}>
          <IncomingInvitesPage roleHint="HELPER" />
        </MobileFullScreenPanel>
      ) : null}
      <ExitConfirmSheet open={exitConfirmOpen} onClose={() => setExitConfirmOpen(false)} />
    </>
  )
}

export const AppMobile = () => {
  const [routeView, setRouteView] = useState<HelperShellView>(helperViewFromPath)
  const { activeWorkspaceId, isWorkspaceLoading } = useWorkspace()
  const showNoWorkspaceShell = !activeWorkspaceId && !isWorkspaceLoading

  useEffect(() => {
    captureJoinTokenFromIntent()
    const onShellIntent = (event: Event) => {
      const detail = (event as CustomEvent<{ url?: string }>).detail
      const token = detail?.url ? parseJoinToken(detail.url) : null
      if (token) stashPendingLinkToken(token)
    }
    window.addEventListener('app_shell_intent', onShellIntent)
    startSyncWorker()
    return () => {
      window.removeEventListener('app_shell_intent', onShellIntent)
      stopSyncWorker()
    }
  }, [])

  useEffect(() => {
    const update = () => setRouteView(helperViewFromPath())
    window.addEventListener('popstate', update)
    return () => window.removeEventListener('popstate', update)
  }, [])

  return (
    <RoleProtectedRoute allowedRoles={['HELPER']} redirectTo="/">
      <ToastProvider>
        <BackStackProvider>
          <div className="min-h-screen bg-[var(--bg)] flex items-stretch justify-center">
            <div className="w-full bg-[var(--surface)] flex flex-col">
              {showNoWorkspaceShell ? (
                <HelperNoWorkspaceShell />
              ) : (
                <HelperDashboardLayout routeView={routeView} />
              )}
              <ToastViewport />
              <WorkspaceSwitchGuardSheet />
              <WorkspaceAccessLostSheet />
            </div>
          </div>
        </BackStackProvider>
      </ToastProvider>
    </RoleProtectedRoute>
  )
}
